import React, { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IFormObject, ITranslation } from 'sierra'
import { Autocomplete, Box, Button, Divider, Grid2, IconButton, Step, StepLabel, Stepper, styled, TextField, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useApp } from '../contexts/AppContext'
import { useAuth } from '../hooks/useAuth'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});


const StepN = memo(({ step, stepIndex, setForm, readOnly }: { step: IFormObject, stepIndex: number, setForm?: Dispatch<SetStateAction<IFormObject[]>>, readOnly: boolean }) => {
    const { lang, translate } = useApp()

    if (!step.fields) step.fields = []

    const [errors, setErrors] = useState<boolean[]>(step.fields?.map(f => false))

    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!step.fields) step.fields = []

    const handleFieldValue = useCallback((value: any, fieldIndex: number) => {
        if (!setForm) return
        setForm(prevForm => {
            const newSteps = [...prevForm];

            if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
            const newFields = [...newSteps[stepIndex].fields];

            newFields[fieldIndex] = {
                ...newFields[fieldIndex],
                field_value: value,
            };

            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                fields: newFields,
            };

            return newSteps;
        })
    }, [])

    const handleDeleteFieldValue = useCallback((fieldIndex: number) => {
        if (!setForm) return
        setForm(prevForm => {
            const newSteps = [...prevForm];

            if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
            const newFields = [...newSteps[stepIndex].fields];

            // If the field exists and has a 'field_value' key, delete it
            if (newFields[fieldIndex]) {
                delete newFields[fieldIndex].field_value;
            }

            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                fields: newFields,
            };

            return newSteps;
        });
    }, []);


    const readUploadFile = useCallback((e: React.ChangeEvent<HTMLInputElement>, fieldIndex: number) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onload = (e) => {
                const data = e.target?.result as string | null | undefined;
                handleFieldValue(data, fieldIndex);
            };
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }, [])

    const getBase64FileSize = (base64String: string) => {
        // Get the length of the base64 string excluding the "data:image/png;base64," part
        const base64Length = base64String.length - base64String.indexOf(',') - 1;

        // Calculate the file size in bytes
        const fileSizeInBytes = (base64Length * 3) / 4;

        // Format the file size into bytes, KB, or MB
        if (fileSizeInBytes < 1024) {
            return `${fileSizeInBytes} bytes`;
        } else if (fileSizeInBytes < 1024 * 1024) {
            return `${(fileSizeInBytes / 1024).toFixed(2)} KB`; // Convert to KB
        } else {
            return `${(fileSizeInBytes / (1024 * 1024)).toFixed(2)} MB`; // Convert to MB
        }
    };

    useEffect(() => console.log('errors array: ' + errors), [errors])

    return (
        <Grid2 container flexDirection={'column'} spacing={4} padding={2} flexWrap={'nowrap'} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
            {step.fields
                // .concat(step.fields)
                .map((f, fi) =>
                    <React.Fragment key={fi}>
                        {fi !== 0 && <Divider flexItem sx={{ backgroundColor: t => t.palette.primary.main }} />}
                        <Grid2 container flexDirection={'column'} spacing={2}>
                            <Grid2>
                                {
                                    ['text', 'number'].includes(f.field_type) ?
                                        <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                            <Grid2 size={'auto'}>
                                                <Typography variant='body1' fontWeight={'bold'} color='primary'>{f.field_label[lang]}: </Typography>
                                            </Grid2>
                                            <Grid2 size={'grow'}>
                                                <TextField
                                                    fullWidth
                                                    error={errors[fi]}
                                                    onBlur={() =>
                                                        setErrors(prev => {
                                                            const newPrev = [...prev]
                                                            newPrev[fi] = !f.field_required ? false : !f.field_value ? true : false
                                                            return newPrev
                                                        })
                                                    }
                                                    disabled={readOnly || (step.step_status && step.step_status !== 'pending')}
                                                    size='small'
                                                    type={f.field_type}
                                                    required={f.field_required}
                                                    label={`${f.field_label[lang]} ${f.field_type === 'number' ? `(${translate("Number")})` : ''}`.trim()}
                                                    placeholder={f.field_placeholder && f.field_placeholder[lang] || ''}
                                                    multiline={f.field_multiline > 1}
                                                    rows={f.field_multiline}
                                                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                                    value={f.field_value || ''}
                                                    onChange={e => handleFieldValue(e.target.value, fi)}
                                                />
                                            </Grid2>
                                        </Grid2>
                                        :
                                        f.field_type === 'file' ?
                                            <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                                <Grid2>
                                                    <Typography variant='body1' fontWeight={'bold'} color='primary'>{f.field_label[lang]}</Typography>
                                                </Grid2>
                                                <Grid2>
                                                    <Button component="label" fullWidth variant='outlined' sx={{ borderRadius: 2, fontWeight: 'bold', borderWidth: '2px' }}
                                                        disabled={
                                                            // (readOnly && !f.field_value) || (step.step_status && step.step_status !== 'pending')
                                                            !readOnly ? !step.step_status ? false : step.step_status === 'pending' ? false : true
                                                                : !f.field_value ? true : false
                                                        }
                                                        onClick={() => readOnly ? window.open(f.field_value as string, '_blank') : undefined}
                                                        endIcon={f.field_required ? '*' : undefined}
                                                    >
                                                        {readOnly ? !f.field_value ? translate('No File Provided') : translate('Download File') : f.field_value ? translate('Re-Upload File') : translate('Upload File')}
                                                        {!readOnly && <VisuallyHiddenInput
                                                            // accept="image/*" // Limit to only accept image files
                                                            required
                                                            type="file"
                                                            onChange={e => readUploadFile(e, fi)} />}
                                                    </Button>
                                                </Grid2>
                                                {f.field_value && !readOnly &&
                                                    <>
                                                        <Grid2>
                                                            <Typography variant='body2' color='grey'>{getBase64FileSize(f.field_value as string)}</Typography>
                                                        </Grid2>
                                                        <Grid2>
                                                            <IconButton
                                                                disabled={readOnly && !f.field_value || (step.step_status && step.step_status !== 'pending')}
                                                                onClick={() => handleDeleteFieldValue(fi)}>
                                                                <Close />
                                                            </IconButton>
                                                        </Grid2>
                                                    </>
                                                }
                                            </Grid2>
                                            :
                                            f.field_type === 'select' ?
                                                <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                                    <Grid2 size={'auto'}>
                                                        <Typography variant='body1' fontWeight={'bold'} color='primary'>{f.field_label[lang]}: </Typography>
                                                    </Grid2>
                                                    <Grid2 size={'grow'}>
                                                        <Autocomplete
                                                            disabled={readOnly || (step.step_status && step.step_status !== 'pending')}
                                                            options={f.field_options || []}
                                                            getOptionLabel={(option) => option && option[lang] || ''}
                                                            id="controlled-demo"
                                                            value={
                                                                typeof f.field_value === 'object' && !Array.isArray(f.field_value) && f.field_value !== null
                                                                    ? f.field_value // If `f.field_value` is an object, directly use it as value
                                                                    : null
                                                            }
                                                            onChange={(event: React.SyntheticEvent, newValue: ITranslation | null | undefined) => {
                                                                if (newValue !== undefined) {
                                                                    handleFieldValue(newValue, fi);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setErrors(prev => {
                                                                    const newPrev = [...prev]
                                                                    newPrev[fi] = !f.field_required ? false : !f.field_value ? true : false
                                                                    return newPrev
                                                                })
                                                            }
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField {...params}
                                                                    error={errors[fi]}
                                                                    variant="outlined"
                                                                    size='small'
                                                                    type={f.field_type}
                                                                    required={f.field_required}
                                                                    label={f.field_label[lang]}
                                                                    placeholder={f.field_placeholder && f.field_placeholder[lang] || ''}
                                                                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                                                />
                                                            )}
                                                        />
                                                    </Grid2>
                                                </Grid2>
                                                :
                                                f.field_type === 'multiselect' ?
                                                    <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                                        <Grid2 size={'auto'}>
                                                            <Typography variant='body1' fontWeight={'bold'} color='primary'>{f.field_label[lang]}: </Typography>
                                                        </Grid2>
                                                        <Grid2 size={'grow'}>
                                                            <Autocomplete
                                                                disabled={readOnly || (step.step_status && step.step_status !== 'pending')}
                                                                multiple
                                                                options={f.field_options || []}
                                                                getOptionLabel={(option) => {
                                                                    // Check if 'option' is of type ITranslation and has the specified language
                                                                    if ((option as ITranslation)[lang] !== undefined) {
                                                                        return (option as ITranslation)[lang] || ''; // Return the translated string or empty string if undefined
                                                                    }
                                                                    return ''; // Return an empty string if 'option' is not of type ITranslation
                                                                }}
                                                                id="controlled-demo"
                                                                value={
                                                                    Array.isArray(f.field_value) ? f.field_value : (f.field_value ? [f.field_value] : [])
                                                                }
                                                                onChange={(event: React.SyntheticEvent, newValue: (ITranslation | string | number)[]) => {
                                                                    // Filter only valid ITranslation items
                                                                    const validValues = newValue.filter((item) => item && typeof item === 'object' && 'en' in item);
                                                                    handleFieldValue(validValues as ITranslation[], fi); // Typecast to ITranslation[]

                                                                    setErrors(prev => {
                                                                        const newPrev = [...prev]
                                                                        newPrev[fi] = !f.field_required ? false : !validValues || (Array.isArray(validValues) && validValues.length === 0) ? true : false
                                                                        return newPrev
                                                                    })
                                                                }}

                                                                onBlur={() => {
                                                                    setErrors(prev => {
                                                                        const newPrev = [...prev]
                                                                        newPrev[fi] = !f.field_required ? false : !f.field_value || (Array.isArray(f.field_value) && f.field_value.length === 0) ? true : false
                                                                        return newPrev
                                                                    })
                                                                }
                                                                }

                                                                renderInput={(params) => (
                                                                    <TextField {...params}
                                                                        error={errors[fi]}
                                                                        variant="outlined"
                                                                        size='small'
                                                                        type={f.field_type}
                                                                        required={f.field_required}
                                                                        label={f.field_label[lang]}
                                                                        placeholder={f.field_placeholder && f.field_placeholder[lang] || ''}
                                                                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                                                    />
                                                                )}
                                                            />
                                                        </Grid2>
                                                    </Grid2>
                                                    : f.field_type === 'date' ?
                                                        <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                                            <Grid2 size={'auto'}>
                                                                <Typography variant='body1' fontWeight={'bold'} color='primary'>{f.field_label[lang]}: </Typography>
                                                            </Grid2>
                                                            <Grid2 size={'grow'}>
                                                                <DatePicker
                                                                    readOnly={readOnly || (step.step_status && step.step_status !== 'pending')}
                                                                    label={f.field_label[lang]}
                                                                    value={f.field_value ? dayjs(f.field_value as string) : null}
                                                                    onChange={(newValue: Dayjs | null) => {
                                                                        if (newValue) {
                                                                            const stringValue = newValue.format('MM-DD-YYYY')
                                                                            handleFieldValue(stringValue, fi);
                                                                        } else {
                                                                            // If no date is selected, set the state to null
                                                                            handleFieldValue(null, fi);
                                                                        }
                                                                        setErrors(prev => {
                                                                            const newPrev = [...prev]
                                                                            newPrev[fi] = !f.field_required ? false : !newValue ? true : false
                                                                            return newPrev
                                                                        })
                                                                    }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            disabled: readOnly || (step.step_status && step.step_status !== 'pending'),
                                                                            required: f.field_required,
                                                                            error: errors[fi], // Set error state
                                                                            helperText: errors[fi] ? 'This field is required' : '', // Show error message
                                                                        },
                                                                    }}
                                                                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                                                />
                                                            </Grid2>
                                                        </Grid2>
                                                        :
                                                        <></>
                                }
                            </Grid2>
                            <Grid2>
                                <Typography variant='body2' sx={{ color: t => t.palette.secondary.dark }}>{f.field_desc[lang]}</Typography>
                            </Grid2>
                        </Grid2>
                    </React.Fragment>

                )}
        </Grid2>
    )
})

// export interface IStepper {
//     step_name: ITranslation;
//     fields: IField[];
//     step_status?: TStepStatus;
//     pending_reason?: string;
// }

const OrderStepper = ({ form, setForm, readOnly = false, setStepIndex }: { form: IFormObject[], setForm?: Dispatch<SetStateAction<IFormObject[]>>, readOnly?: boolean, setStepIndex?: Dispatch<SetStateAction<number>> }) => {
    const { user } = useAuth()
    const { lang, translate } = useApp()

    const [activeStep, setActiveStep] = useState<number>(0);

    const steps = useMemo(() => form.map((s, i) => ({
        name: s.step_name, component: <StepN key={i} step={s} stepIndex={i} setForm={setForm} readOnly={readOnly} />,
        isComplete: (): boolean => (
            s.fields
                && s.fields[0]
                && s.fields.every(f =>
                    f.field_required === false ? true :
                        !f.field_value ? false :
                            f.field_type === 'multiselect' && Array.isArray(f.field_value) && f.field_value.length === 0 ? false : true
                )
                ? true : false
        )
    })), [form, setForm, readOnly])

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };

    useEffect(() => {
        if (setStepIndex) setStepIndex(activeStep)
    }, [activeStep])

    useEffect(() => console.log(form), [form])

    return (
        <Grid2 container size={'grow'} height={'100%'} padding={2} flexDirection={'column'} spacing={4}>
            <Grid2 container size={'grow'} flexDirection={'column'} gap={3} flexWrap={'nowrap'} overflow={'hidden'}>
                <Grid2>
                    <Stepper nonLinear activeStep={activeStep}>
                        {steps.map((step, index) => (
                            <Step key={index} completed={user?.role === 'customer' && form[index].step_status === 'pending' ? false : step.isComplete()}
                                sx={{
                                    '& .MuiStepLabel-root .Mui-completed': {
                                        color: 'green', // circle color (COMPLETED)
                                    },
                                    '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
                                        color: 'grey.500', // text label (COMPLETED)
                                    },
                                    '& .MuiStepLabel-root .Mui-active': {
                                        color: 'secondary.dark', // circle color (ACTIVE)
                                    },
                                    '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
                                        color: 'common.white', // text label (ACTIVE)
                                    },
                                    '& .MuiStepLabel-root .Mui-active .MuiStepIcon-text': {
                                        fill: 'white', // circle's number (ACTIVE)
                                    },
                                }}
                            >
                                <StepLabel
                                    onClick={handleStep(index)}
                                    sx={{ cursor: 'pointer' }}
                                    error={form[index].step_status === 'pending'}
                                    optional={
                                        user?.role === 'customer' ? form[index].step_status === 'pending' ?
                                            <Typography variant="caption" color="error">{form[index].pending_reason || translate('Update Required')}</Typography>
                                            : form[index].step_status === 'awaiting' ? <Typography variant="caption" color="warning">{translate("Awaiting External Validation")}</Typography>
                                                : form[index].step_status === 'completed' ? <Typography variant="caption" color="success">{translate("Completed")}</Typography>
                                                    : form[index].step_status === 'validating' ? <Typography variant="caption" color="info">{translate("Awaiting Internal Validation")}</Typography>
                                                        : undefined : undefined}
                                >{step.name[lang]}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Grid2>
                {steps.map((step, index) => index == activeStep ? step.component : null)}
                <Grid2 marginTop={'auto'}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, alignItems: 'center', gap: 2 }}>
                        <Button
                            color={"inherit"}
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ mr: 1 }}
                        >
                            {translate("Previous Step")}
                        </Button>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button onClick={handleNext} sx={{ mr: 1 }} disabled={activeStep === steps.length - 1}>
                            {translate("Next Step")}
                        </Button>
                    </Box>
                </Grid2>
            </Grid2>


        </Grid2>
    )
}

export default OrderStepper